/** Keep oversized diagnostic values from making the collector reject a span. */
const encoder = new TextEncoder();
const SPAN_DATA_BYTES = 16_384;

function bytes(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).byteLength;
}

function boundedData(data: Record<string, unknown>, budget: number): Record<string, unknown> {
  let remaining = bytes(data);
  if (remaining <= budget) return data;

  const result = { ...data };
  const largestFirst = Object.entries(data)
    .map(([key, value]) => ({ key, size: bytes(value) }))
    .sort((a, b) => b.size - a.size);
  for (const entry of largestFirst) {
    if (remaining <= budget) break;
    if (entry.size <= 64) continue;
    const replacement = `[omitted: ${entry.size} bytes]`;
    result[entry.key] = replacement;
    remaining -= entry.size - bytes(replacement);
  }
  const omitted = new Set<string>();
  for (const { key } of largestFirst) {
    if (remaining <= budget - 64) break;
    remaining -= bytes(key) + bytes(result[key]) + 2;
    omitted.add(key);
  }
  if (!omitted.size) return result;
  return {
    ...Object.fromEntries(Object.entries(result).filter(([key]) => !omitted.has(key))),
    'ricky.telemetry.omitted_attributes': omitted.size,
  };
}

type TelemetrySpan = {
  attributes?: Record<string, unknown>;
  data?: Record<string, unknown>;
  description?: string;
  name?: string;
};

/** Preserve span identity, timing, status and small attributes; bound only diagnostics. */
export function limitTelemetrySpan<T extends TelemetrySpan>(span: T, budget = SPAN_DATA_BYTES): T {
  return {
    ...span,
    ...(span.name && bytes(span.name) > Math.min(2_048, budget)
      ? { name: `${span.name.slice(0, Math.min(256, Math.floor(budget / 8)))} [truncated]` }
      : {}),
    ...(span.description && bytes(span.description) > Math.min(2_048, budget)
      ? {
          description: `${span.description.slice(0, Math.min(256, Math.floor(budget / 8)))} [truncated]`,
        }
      : {}),
    ...(span.attributes ? { attributes: boundedData(span.attributes, budget) } : {}),
    ...(span.data ? { data: boundedData(span.data, budget) } : {}),
  };
}
